import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
	canCreateInvoice,
	formatCoins,
	formatCurrency,
	formatINR,
	getInvoiceBadge,
	getInvoiceCurrency,
	getInvoiceNative,
	getPartnerDueDate,
	getPartnerGrossAmount,
	getPartnerNetReceived,
	getPartnerThresholdProgress,
	INVOICE_FIELDS,
	getReceivedPercentage,
	GROSS_INVOICE_AMOUNT_TOOLTIP,
	isPartnerAwaitingInvoice,
	NET_REVENUE_TOOLTIP,
	PARTNER_INVOICE_CB_TOOLTIP,
	REVENUE_CHARGEBACK_TOOLTIP,
	shouldShowInvoiceAmounts,
	shouldShowPartnerDueDate,
} from "./revenueSummaryUtils";

const CELL_COLOR_CLASS = {
	green: "rs-cell-value--green",
	amber: "rs-cell-value--amber",
	blue: "rs-cell-value--blue",
	red: "rs-cell-value--red",
	indigo: "rs-cell-value--indigo",
};

const emptyCell = () => <span className="rs-text-muted">—</span>;

const CurrencyBadge = ({ currency }) => (
	<span className={`rs-currency-badge rs-currency-badge--${currency === "INR" ? "inr" : "usd"}`}>
		{currency}
	</span>
);

const getPartnerSortValue = (partner, key) => {
	if (key === "partnerName") return partner.partnerName || "";
	if (key.startsWith("invoice.")) {
		const field = key.slice("invoice.".length);
		return partner.invoice?.[field] ?? 0;
	}
	return partner[key] ?? 0;
};

const PartnersRevenueTable = ({ partners = [], internalEarnings, selectedMonth }) => {
	const [currencyFilter, setCurrencyFilter] = useState("ALL");
	const [sortConfig, setSortConfig] = useState({ key: "ourRevenueCoins", direction: "desc" });

	const filteredPartners = useMemo(() => {
		if (currencyFilter === "ALL") return partners;
		return partners.filter((p) => p.currency === currencyFilter);
	}, [partners, currencyFilter]);

	const sortedPartners = useMemo(() => {
		const sorted = [...filteredPartners];
		sorted.sort((a, b) => {
			const aVal = getPartnerSortValue(a, sortConfig.key);
			const bVal = getPartnerSortValue(b, sortConfig.key);
			if (sortConfig.key === "partnerName" || sortConfig.key === "invoice.invoiceEligibility") {
				const cmp = String(aVal).localeCompare(String(bVal));
				return sortConfig.direction === "asc" ? cmp : -cmp;
			}
			return sortConfig.direction === "asc" ? aVal - bVal : bVal - aVal;
		});
		return sorted;
	}, [filteredPartners, sortConfig]);

	const handleSort = (key) => {
		setSortConfig((prev) => ({
			key,
			direction: prev.key === key && prev.direction === "desc" ? "asc" : "desc",
		}));
	};

	const renderSortTh = (key, label, align = "left", title) => {
		const active = sortConfig.key === key;
		return (
			<th onClick={() => handleSort(key)} title={title} className={`rs-sort-th rs-text-${align === "right" ? "right" : align === "center" ? "center" : "left"}`}>
				{label}
				{active && <span className="rs-sort-indicator">{sortConfig.direction === "asc" ? "▲" : "▼"}</span>}
			</th>
		);
	};

	const renderNativeCell = (partner, field, colorKey) => (
		<div className={`rs-cell-value ${colorKey ? CELL_COLOR_CLASS[colorKey] : ""}`}>
			{formatCurrency(partner[field] ?? 0, partner.currency)}
		</div>
	);

	const renderInvoiceNativeCell = (partner, field, colorKey) => (
		<div className={`rs-cell-value ${colorKey ? CELL_COLOR_CLASS[colorKey] : ""}`}>
			{formatCurrency(getInvoiceNative(partner, field), getInvoiceCurrency(partner))}
		</div>
	);

	const renderInvoiceBadge = (partner) => {
		const badge = getInvoiceBadge(partner);
		const badgeStyle = {
			"--rs-badge-bg": badge.backgroundColor,
			"--rs-badge-color": badge.color,
		};
		if (badge.isLink && badge.href) {
			return (
				<a
					href={badge.href}
					target="_blank"
					rel="noopener noreferrer"
					className="rs-invoice-badge"
					style={badgeStyle}
				>
					{badge.label} ↗
				</a>
			);
		}
		return (
			<span className="rs-invoice-badge" style={badgeStyle} title={badge.tooltip}>
				{badge.label}
			</span>
		);
	};

	const renderThreshold = (partner) => {
		if (partner.currency === "INR") return emptyCell();
		const tp = getPartnerThresholdProgress(partner);
		if (!tp) return emptyCell();
		return (
			<div className="rs-threshold-cell">
				<div className="rs-threshold-main">
					{formatCurrency(tp.accumulatedRevenueUSD ?? 0, "USD")} / {formatCurrency(tp.thresholdAmountUSD ?? 0, "USD")}
				</div>
				<div className="rs-threshold-sub">{tp.progressPercent ?? 0}% to threshold</div>
			</div>
		);
	};

	return (
		<div>
			<div className="rs-partners-header">
				<h3 className="rs-title">Partner Revenue Breakdown</h3>
				<div className="rs-partners-filters">
					{["ALL", "INR", "USD"].map((f) => (
						<button
							key={f}
							type="button"
							onClick={() => setCurrencyFilter(f)}
							className={`rs-filter-btn ${currencyFilter === f ? "rs-filter-btn--active" : "rs-filter-btn--inactive"}`}
						>
							{f === "ALL" ? "All" : f}
						</button>
					))}
				</div>
			</div>

			<div className="table-container-v4">
				<table className="data-table-v4 rs-partners-table">
					<thead>
						<tr>
							<th rowSpan={2} className="rs-text-center">Partner</th>
							<th rowSpan={2} className="rs-text-center">Currency</th>
							<th colSpan={3} className="rs-group-header">Our Stats</th>
							<th colSpan={2} className="rs-group-header rs-group-header--border">User Payout</th>
							<th colSpan={2} className="rs-group-header rs-group-header--border">Metrics</th>
							<th colSpan={7} className="rs-group-header rs-group-header--border">Invoice</th>
						</tr>
						<tr>
							{renderSortTh("ourRevenueCoins", "Gross", "right")}
							{renderSortTh("chargebackCoins", "Rev CB", "right", REVENUE_CHARGEBACK_TOOLTIP)}
							{renderSortTh("netRevenueCoins", "Net Rev", "right", NET_REVENUE_TOOLTIP)}
							<th className="rs-text-right rs-th-border-left">Payout (₹)</th>
							{renderSortTh("marginCoins", "Margin", "right")}
							{renderSortTh("userRevSharePercent", "Share %", "right")}
							<th className="rs-text-center">Threshold</th>
							{renderSortTh("invoice.invoiceEligibility", "Status", "center")}
							{renderSortTh(`invoice.${INVOICE_FIELDS.reported}`, "Reported", "right")}
							{renderSortTh(`invoice.${INVOICE_FIELDS.chargebacks}`, "Partner CB", "right", PARTNER_INVOICE_CB_TOOLTIP)}
							{renderSortTh(`invoice.${INVOICE_FIELDS.gross}`, "Gross Amt", "right", GROSS_INVOICE_AMOUNT_TOOLTIP)}
							{renderSortTh(`invoice.${INVOICE_FIELDS.received}`, "Received", "right")}
							<th className="rs-text-center">Due</th>
							<th className="rs-text-center">Create</th>
						</tr>
					</thead>
					<tbody>
						{sortedPartners.map((partner, idx) => {
							const showAmounts = shouldShowInvoiceAmounts(partner);
							const awaiting = isPartnerAwaitingInvoice(partner, selectedMonth);
							const marginColor = partner.marginNative >= 0 ? "green" : "red";
							return (
								<tr key={partner.partnerId || idx} className={awaiting ? "rs-row-awaiting" : ""}>
									<td className="rs-partner-name-cell">
										<div className="rs-partner-name">{partner.partnerName}</div>
										{awaiting && <div className="rs-awaiting-warning">⚠ Awaiting invoice</div>}
									</td>
									<td><CurrencyBadge currency={partner.currency} /></td>
									<td className="rs-text-right">
										{renderNativeCell(partner, "ourRevenueNative", "green")}
										<div className="rs-cell-sub">{formatCoins(partner.ourRevenueCoins)}</div>
									</td>
									<td className="rs-text-right" title={REVENUE_CHARGEBACK_TOOLTIP}>
										{renderNativeCell(partner, "chargebackNative", "amber")}
										<div className="rs-cell-sub">{formatCoins(partner.chargebackCoins)}</div>
									</td>
									<td className="rs-text-right" title={NET_REVENUE_TOOLTIP}>
										{renderNativeCell(partner, "netRevenueNative", "blue")}
										<div className="rs-cell-sub">{formatCoins(partner.netRevenueCoins)}</div>
									</td>
									<td className="rs-text-right rs-td-border-left">
										<div className="rs-cell-value">{formatINR(partner.userPayoutINR)}</div>
										<div className="rs-cell-sub">{formatCoins(partner.userPayoutCoins)}</div>
									</td>
									<td className="rs-text-right">
										{renderNativeCell(partner, "marginNative", marginColor)}
									</td>
									<td className="rs-text-right rs-td-border-left">
										<span className="rs-share-pill">{partner.userRevSharePercent ?? 0}%</span>
									</td>
									<td className="rs-text-center rs-td-border-left">{renderThreshold(partner)}</td>
									<td className="rs-text-center">{renderInvoiceBadge(partner)}</td>
									<td className="rs-text-right">
										{showAmounts ? renderInvoiceNativeCell(partner, INVOICE_FIELDS.reported) : emptyCell()}
									</td>
									<td className="rs-text-right" title={PARTNER_INVOICE_CB_TOOLTIP}>
										{showAmounts ? renderInvoiceNativeCell(partner, INVOICE_FIELDS.chargebacks, "amber") : emptyCell()}
									</td>
									<td className="rs-text-right" title={GROSS_INVOICE_AMOUNT_TOOLTIP}>
										{showAmounts ? renderInvoiceNativeCell(partner, INVOICE_FIELDS.gross, "indigo") : emptyCell()}
									</td>
									<td className="rs-text-right">
										{showAmounts ? (
											<>
												{renderInvoiceNativeCell(partner, INVOICE_FIELDS.received, "green")}
												{getPartnerGrossAmount(partner) > 0 && (
													<div className="rs-cell-sub">
														{getReceivedPercentage(getPartnerNetReceived(partner), getPartnerGrossAmount(partner))}% received
													</div>
												)}
											</>
										) : emptyCell()}
									</td>
									<td className="rs-text-center">
										{shouldShowPartnerDueDate(partner) ? getPartnerDueDate(partner) : emptyCell()}
									</td>
									<td className="rs-text-center">
										{canCreateInvoice(partner) ? (
											<Link to="/admin/invoice" className="rs-create-link">Create</Link>
										) : emptyCell()}
									</td>
								</tr>
							);
						})}
					</tbody>
				</table>
			</div>

			{internalEarnings && (internalEarnings.totalUserPayoutINR ?? 0) > 0 && (
				<div className="rs-internal-earnings">
					<h4 className="rs-internal-earnings-title">{internalEarnings.partnerName || "Internal Earnings"}</h4>
					<div className="rs-internal-earnings-value">{formatINR(internalEarnings.totalUserPayoutINR)}</div>
					<div className="rs-internal-earnings-meta">{formatCoins(internalEarnings.totalUserPayoutCoins)} coins</div>
				</div>
			)}
		</div>
	);
};

export default PartnersRevenueTable;
